import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { WALID_LOGO } from "@/data/assets";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/ai-solutions", label: "AI Solutions" },
    { to: "/results", label: "Results" },
    { to: "/about", label: "About" },
    { to: "/blog", label: "Blog" },
    { to: "/contact", label: "Contact" },
];

function StarLogo({ className = "" }) {
    // unused - kept for fallback, harmless
    return null;
}

export default function SiteHeader() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const loc = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [loc.pathname]);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled || open
                    ? "bg-[color:var(--bg)]/95 backdrop-blur-xl border-b border-[color:var(--line)]"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="container-page flex items-center justify-between h-24 md:h-36">
                <Link
                    to="/"
                    data-testid="logo-link"
                    className="flex items-center group -my-2"
                >
                    <span
                        className="signature-mark w-32 h-32 md:w-48 md:h-48 shrink-0"
                        style={{ "--logo-url": `url(${WALID_LOGO})` }}
                        aria-label="Walid Cheikh"
                    />
                </Link>

                <nav className="hidden md:flex items-center gap-1">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.to === "/"}
                            data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                            className={({ isActive }) =>
                                `px-3 py-2 text-sm font-semibold transition-colors ${
                                    isActive
                                        ? "text-[color:var(--ink)]"
                                        : "text-[color:var(--text-muted)] hover:text-[color:var(--ink)]"
                                }`
                            }
                        >
                            {n.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden md:flex items-center gap-3">
                    <Link
                        to="/contact"
                        data-testid="header-cta-button"
                        className="btn-primary"
                    >
                        Book a Call <ArrowUpRight size={14} />
                    </Link>
                </div>

                <button
                    type="button"
                    className="md:hidden text-[color:var(--ink)] p-2 -mr-2"
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Toggle menu"
                    data-testid="mobile-menu-toggle"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div
                    className="md:hidden border-t border-[color:var(--line)] bg-[color:var(--bg)]"
                    data-testid="mobile-menu"
                >
                    <div className="container-page py-6 flex flex-col gap-1">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.to === "/"}
                                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                                className={({ isActive }) =>
                                    `py-3 text-base font-semibold border-b border-[color:var(--line)] ${
                                        isActive
                                            ? "text-[color:var(--brand-deep)]"
                                            : "text-[color:var(--ink)]"
                                    }`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/contact"
                            className="btn-primary mt-5 justify-center"
                            data-testid="mobile-cta-button"
                        >
                            Book a Call <ArrowUpRight size={14} />
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
